import { useEffect, useState } from 'react';

import { useUnit } from 'effector-react';
import { parse } from 'query-string';
import { useHistory, useLocation } from 'react-router-dom';

import { toaster } from '@m/components-ui/toaster';
import { customerMaxSubscribersCapMessage } from '@m/utils/maxSubscribersCapError';
import { EnterpriseInvitationService } from 'apps/customer/services/EnterpriseInvitationService';

import { setTokenAndUserInfoForEnterpriseInvitation, userStore } from '../state/user';

function removeInvitationToken(pathname: string, search: string, hash: string): string {
  const params = new URLSearchParams(search);
  params.delete('ent_token');
  const remainingSearch = params.toString();
  return `${pathname}${remainingSearch ? `?${remainingSearch}` : ''}${hash}`;
}

/**
 * if isRedeeming is null, then we are not redeeming an invitation.
 */
export function useEnterpriseInvitationRedeem() {
  const history = useHistory();
  const location = useLocation();
  const parsedSearch = parse(location.search);
  const parsedToken = parsedSearch.ent_token;
  const invitationToken = typeof parsedToken === 'string' ? parsedToken : null;
  const { enterpriseUser } = useUnit(userStore);
  const parsedOnboardingToken =
    typeof parsedSearch.token === 'string' ? parsedSearch.token : undefined;
  const parsedEmployeeId =
    typeof parsedSearch.employeeId === 'string' ? parsedSearch.employeeId : undefined;
  const onboardingToken = enterpriseUser?.token || parsedOnboardingToken;
  const employeeId = enterpriseUser?.employeeId || parsedEmployeeId;
  const hasOnboardingToken = !!onboardingToken && !!employeeId;
  const [isRedeeming, setIsRedeeming] = useState<boolean | null>(
    !!invitationToken || hasOnboardingToken || null,
  );
  const [successMessage, setSuccessMessage] = useState<string>();
  const [redemptionError, setRedemptionError] = useState<string>();
  const [retryAttempt, setRetryAttempt] = useState(0);

  useEffect(() => {
    if (!invitationToken && !hasOnboardingToken) {
      return;
    }

    setIsRedeeming(true);
    setRedemptionError(undefined);

    const redeem = async () => {
      if (invitationToken) {
        const response = await EnterpriseInvitationService.redeemInvite({
          token: invitationToken,
        });
        if (!response.success) {
          return { error: response.error, success: false } as const;
        }
        return {
          message: `You have been added to ${response.data.enterpriseName}'s parking subscription.`,
          shouldClearOnboardingToken: false,
          success: true,
        } as const;
      }

      if (!onboardingToken || !employeeId) {
        return { error: 'Could not redeem the parking subscription', success: false } as const;
      }

      const response = await EnterpriseInvitationService.claimRedemptionToken({
        employeeId,
        token: onboardingToken,
      });
      if (!response.success) {
        return { error: response.error, success: false } as const;
      }
      return {
        message: 'You have been added to a company parking subscription.',
        shouldClearOnboardingToken: true,
        success: true,
      } as const;
    };

    void redeem()
      .then((result) => {
        if (!result.success) {
          const message = customerMaxSubscribersCapMessage(result.error);
          setRedemptionError(message);
          toaster.create({ title: message, type: 'error' });
          return;
        }

        setSuccessMessage(result.message);
        // Keep React Router's location in sync so another consumer cannot redeem the token again.
        history.replace(
          result.shouldClearOnboardingToken
            ? location.pathname
            : removeInvitationToken(location.pathname, location.search, location.hash),
        );
        if (result.shouldClearOnboardingToken) {
          setTokenAndUserInfoForEnterpriseInvitation({
            enterpriseUser: { token: '' },
          });
        }
      })
      .catch(() => {
        const message = customerMaxSubscribersCapMessage();
        setRedemptionError(message);
        toaster.create({ title: message, type: 'error' });
      })
      .finally(() => {
        setIsRedeeming(false);
      });
  }, [
    employeeId,
    hasOnboardingToken,
    history,
    invitationToken,
    location.hash,
    location.pathname,
    location.search,
    onboardingToken,
    retryAttempt,
  ]);

  return {
    isRedeemingInvite: isRedeeming,
    inviteSuccessMessage: successMessage,
    inviteRedemptionError: redemptionError,
    retryInviteRedemption: () => setRetryAttempt((attempt) => attempt + 1),
  };
}
